import { API_BASE, SUPABASE_URL, WORKSPACE_DOMAIN } from './config.js';

/**
 * Sign-in through Supabase Auth: Google (implicit flow; the tokens come back in the URL fragment of /auth/callback) or a
 * password (owner 2026-10-28; the Worker talks to Supabase, see Login.tsx). The session is kept on the device
 * (localStorage, so a new tab is signed in too) and the access token is renewed with the refresh token before it runs
 * out, so nobody is signed out after an hour. An older session in sessionStorage is still read.
 */
const KEY = 'kff.session';

export interface Session {
  accessToken: string;
  /** renews the session (Supabase rotates it: each one works once) */
  refreshToken?: string;
  expiresAt: number;
}

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
const local = (): Store | null => {
  try {
    return localStorage;
  } catch {
    return null;
  }
};
const read = (storage?: Pick<Storage, 'getItem'>): Session | null => {
  try {
    if (storage) return JSON.parse(storage.getItem(KEY) ?? 'null') as Session | null;
    return (JSON.parse(local()?.getItem(KEY) ?? 'null') ?? JSON.parse(sessionStorage.getItem(KEY) ?? 'null')) as Session | null;
  } catch {
    return null;
  }
};

/** A session as the Worker returns it (password, refresh, two-step). */
export const sessionFrom = (s: { accessToken: string; refreshToken?: string; expiresIn: number }, now = Date.now()): Session => ({ accessToken: s.accessToken, ...(s.refreshToken ? { refreshToken: s.refreshToken } : {}), expiresAt: now + s.expiresIn * 1000 });

export function googleSignInUrl(origin: string, supabaseUrl = SUPABASE_URL, domain = WORKSPACE_DOMAIN): string {
  const p = new URLSearchParams({ provider: 'google', redirect_to: `${origin}/auth/callback` });
  if (domain) p.set('hd', domain); // hint: pick the Workspace account (the API enforces the domain)
  p.set('prompt', 'select_account'); // always offer the account chooser, so a wrong (private) account can be swapped
  return `${supabaseUrl}/auth/v1/authorize?${p.toString()}`;
}

export function parseCallback(hash: string, now = Date.now()): Session | null {
  const p = new URLSearchParams(hash.replace(/^#/, ''));
  const token = p.get('access_token');
  if (!token) return null;
  const expiresIn = Number(p.get('expires_in') ?? '3600');
  const refresh = p.get('refresh_token');
  return { accessToken: token, ...(refresh ? { refreshToken: refresh } : {}), expiresAt: now + expiresIn * 1000 };
}

export function saveSession(s: Session | null, storage?: Pick<Storage, 'setItem' | 'removeItem'>): void {
  try {
    const targets = storage ? [storage] : [local(), sessionStorage].filter((x): x is Store => !!x);
    for (const st of targets) {
      if (s && st === targets[0]) st.setItem(KEY, JSON.stringify(s));
      else st.removeItem(KEY);
    }
  } catch {
    /* private mode – the session lives only in memory */
  }
}

/** A stored session that has run out: the sign-in page says "you were signed out" instead of showing nothing. */
export function sessionExpired(storage?: Pick<Storage, 'getItem'>, now = Date.now()): boolean {
  const s = read(storage);
  return !!s && s.expiresAt <= now + 30_000 && !s.refreshToken;
}

let renewing: Promise<Session | null> | null = null;
/**
 * The access token to send: renewed first when it runs out within two minutes. The newest session is read from the
 * device each time, so a tab never renews with a refresh token another tab already used; one renewal at a time.
 */
export async function freshToken(fallback: Session, fetchImpl: typeof fetch = (...a) => fetch(...a), now = () => Date.now()): Promise<string> {
  const s = read() ?? fallback;
  if (s.expiresAt > now() + 120_000 || !s.refreshToken) return s.accessToken;
  renewing ??= (async () => {
    try {
      const res = await fetchImpl(`${API_BASE}/api/auth/refresh`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ refreshToken: s.refreshToken }) });
      if (!res.ok) return null;
      const next = sessionFrom((await res.json()) as { accessToken: string; refreshToken: string; expiresIn: number }, now());
      saveSession(next);
      return next;
    } catch {
      return null;
    } finally {
      setTimeout(() => (renewing = null), 0);
    }
  })();
  const next = await renewing;
  // could not renew: the old token answers 401 and the page says "you were signed out"
  return next?.accessToken ?? s.accessToken;
}

/** The email in a Supabase access token, for "x isn't a KFF account". Display only – the API verifies the token. */
export function tokenEmail(token: string): string {
  try {
    const b64 = (token.split('.')[1] ?? '').replace(/-/g, '+').replace(/_/g, '/');
    const email = (JSON.parse(atob(b64)) as { email?: unknown }).email;
    return typeof email === 'string' ? email : '';
  } catch {
    return '';
  }
}

const RETURN_KEY = 'kff.returnTo';

/** Remember where the user was, so the callback can take them back there after Google. */
export function keepReturnTo(path: string, storage: Pick<Storage, 'setItem'> = sessionStorage): void {
  try {
    storage.setItem(RETURN_KEY, path);
  } catch {
    /* private mode – the callback lands on My work */
  }
}

/** The remembered path (same-origin paths only), cleared once read. */
export function takeReturnTo(storage: Pick<Storage, 'getItem' | 'removeItem'> = sessionStorage): string {
  try {
    const p = storage.getItem(RETURN_KEY) ?? '';
    storage.removeItem(RETURN_KEY);
    return p.startsWith('/') && !p.startsWith('//') && !p.startsWith('/auth/') ? p : '/';
  } catch {
    return '/';
  }
}

/** The stored session; one that ran out still counts while it can be renewed. */
export function loadSession(storage?: Pick<Storage, 'getItem'>, now = Date.now()): Session | null {
  const s = read(storage);
  return s && (s.expiresAt > now + 30_000 || !!s.refreshToken) ? s : null;
}
